import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { edgeFunctionUrl } from '@/integrations/supabase/config';
import { useAuth } from './useAuth';
import { Database } from '@/integrations/supabase/types';

type UserRole = Database['public']['Enums']['user_role'];

interface User {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  lastLogin: string | null;
  lastActive: string | null;
  createdAt: string;
  roles: UserRole[];
  primaryRole: UserRole | null;
  tier: 'agency' | 'subaccount';
}

interface InviteUserData {
  email: string;
  fullName: string;
  role: UserRole;
  brandId?: string;
  brandIds?: string[];
  brandRole?: 'member' | 'brand_admin';
  tier?: 'agency' | 'subaccount';
}

interface UpdateRoleData {
  userId: string;
  role: UserRole;
}

export function useIsAdmin() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['is-admin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin',
      });
      
      if (error) {
        if (import.meta.env.DEV) console.error('Error checking admin role:', error);
        return false;
      }
      
      return data === true;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useIsSuperUser() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['is-super-user', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'super_user',
      });

      if (error) {
        if (import.meta.env.DEV) console.error('Error checking super_user role:', error);
        return false;
      }

      return data === true;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUserRole() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        if (import.meta.env.DEV) console.error('Error fetching user role:', error);
        return null;
      }
      
      return data?.role ?? null;
    },
    enabled: !!user?.id,
  });
}

export function useAllUsers() {
  const { session } = useAuth();

  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await fetch(
        edgeFunctionUrl('admin-manage-users'),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch users');
      }

      const data = await response.json();
      return data.users as User[];
    },
    enabled: !!session?.access_token,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (data: InviteUserData) => {
      const response = await fetch(
        edgeFunctionUrl('admin-manage-users'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'invite',
            ...data,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to invite user');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateRoleData) => {
      const response = await fetch(
        edgeFunctionUrl('admin-manage-users'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'update_role',
            ...data,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update role');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useRemoveUser() {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(
        edgeFunctionUrl('admin-manage-users'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'remove',
            userId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove user');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useSendPasswordReset() {
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (email: string) => {
      const response = await fetch(
        edgeFunctionUrl('admin-manage-users'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'send_reset_email',
            email,
            redirectTo: `${window.location.origin}/reset-password`,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send password reset email');
      }

      return response.json();
    },
  });
}

export function useUpdateUserTier() {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  return useMutation({
    mutationFn: async ({ userId, tier }: { userId: string; tier: 'agency' | 'subaccount' }) => {
      const response = await fetch(
        edgeFunctionUrl('admin-manage-users'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'update_tier', userId, tier }),
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update tier');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}
